import { InfoCircle } from '@assets/svgs';
import { Box, FormControl } from '@mui/material';
import { PolicyCoverages, PolicyCoveragesCodeNames } from '@models/insuranceLine';
import { Checkbox, FormControlLabel, FormHelperText } from '@components/index';
import { CollapsedField } from '@components/quoteDetails/CollapsedField';
import { QFCTypeExecuted } from '@components/quoteDetails/QuoteFieldControl';
import { adjustFontSize, adjustLineHeight } from '@theme/styleOverrides/typography';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { DeprecatedIcon } from '@components/tooltip/DeprecatedIcon';
import { DEPRECATED_TOOLTIP_TEXT } from '@components/tooltip';

export interface CheckboxFieldProps extends QFCTypeExecuted {
  onBlur: () => void;
  isRequired: boolean;
  isRequiredBeforeBind?: boolean;
}

export const CheckboxField = ({
  setValue,
  attributeType,
  errorMessage,
  value,
  isCollapsed,
  isRequired,
  isRequiredBeforeBind,
  hoverText,
  isReadOnly,
  onBlur,
  disabledPolicyCoverages,
  externalIcon,
}: CheckboxFieldProps) => {
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences, state.setLayoutPreferences], shallow);

  let isCheckboxDisabled = false;

  if (PolicyCoveragesCodeNames.includes(attributeType.codeName)) {
    const newValue: boolean = disabledPolicyCoverages[attributeType.codeName as keyof PolicyCoverages];
    isCheckboxDisabled = newValue;
  }
  // TODO: Validate value type instead of casting to Boolean
  if (isCollapsed) {
    return (
      <CollapsedField label={attributeType.label} value={value ? 'Yes' : 'No'} deprecated={attributeType.deprecated} />
    );
  }
  return (
    <FormControl
      error={!!errorMessage}
      sx={{
        '& .MuiFormHelperText-root': {
          marginLeft: '24px',
        },
      }}
    >
      <FormControlLabel
        disabled={isReadOnly || isCheckboxDisabled}
        sxProps={{
          formControlLabel: {
            paddingTop: 1,
            '&.MuiFormControlLabel-root': {
              marginLeft: 0,
              alignItems: 'flex-start',
              '.MuiTypography-root': {
                color: errorMessage ? 'error.main' : undefined,
              },
              '.MuiStack-root': {
                display: 'block',
                paddingTop: '10px',
                lineHeight: `${adjustLineHeight(14, layoutPreferences.fontSize)}px`,
                margin: '0px',
              },
            },
            '& .MuiFormControlLabel-label': {
              lineHeight: `${adjustLineHeight(14, layoutPreferences.fontSize)}px`,
              fontSize: `${adjustFontSize(14, layoutPreferences.fontSize)}px`,
              textDecoration: attributeType.deprecated ? 'line-through' : 'none',
            },
          },
        }}
        required={false}
        control={
          <>
            <Checkbox
              sx={{
                marginLeft: '-9px', // 9px is number of padding on box so we are 'shifting'
                marginTop: '-10px', // 10px  one extra here to align with label more 'center' than just top alignment
              }}
              hasError={!!errorMessage}
              onBlur={onBlur}
              checked={Boolean(value)}
              onChange={(e) => setValue(e.target.checked)}
              disabled={isReadOnly || isCheckboxDisabled}
            />
          </>
        }
        label={(() => {
          const infoIcon =
            hoverText && !externalIcon ? (
              <InfoCircle
                style={{
                  position: 'absolute',
                  top: -1,
                  width: '15px',
                  height: '15px',
                  marginLeft: '4px',
                }}
              />
            ) : null;
          return (
            <Box
              sx={
                externalIcon
                  ? {
                      display: 'inline-flex',
                      alignItems: 'center',
                      '& .MuiButtonBase-root': { marginTop: '0px' },
                      gap: 0.5,
                    }
                  : {}
              }
            >
              <Box>
                {attributeType.deprecated ? (
                  <DeprecatedIcon
                    iconStyle={{
                      top: '3px',
                      marginTop: '-6px',
                      marginRight: '4px',
                    }}
                  />
                ) : null}
                {attributeType.label}
                {isRequired ? '*' : isRequiredBeforeBind ? '†' : ''}
                <Box sx={{ display: 'inline', position: 'relative', height: '0' }}>{infoIcon}</Box>
              </Box>
              {externalIcon}
            </Box>
          );
        })()}
        tooltipTitle={
          attributeType.deprecated
            ? hoverText
              ? `${DEPRECATED_TOOLTIP_TEXT}\n\n${hoverText}`
              : DEPRECATED_TOOLTIP_TEXT
            : !externalIcon
              ? hoverText
              : null
        }
      />
      <FormHelperText>{errorMessage ?? ' '}</FormHelperText>
    </FormControl>
  );
};
